import classNames from 'classnames/bind';
import Select, { DropdownIndicatorProps, OptionProps, components } from 'react-select';
import Createable from 'react-select/creatable';

import { ChevronDown, ChevronUp } from '../../assets/icons/Icons';
// Sub Objects
import Styles from './dropdown.module.scss';

const cx = classNames.bind(Styles);

const COLORS = {
  black: '#15191B',
  lightGrey: '#EDEDED',
  disabledGrey: '#EBEBEB',
  disabledText: '#B8B8B8',
  mediumGrey: '#747474',
  borderGrey: '##e3e3e3',
  white: '#FFFFFF',
};

/** Emotion Documentation for CSS-in-JS.
 * https://emotion.sh/docs/media-queries
 *
 *  React-Select Documentation.
 * https://react-select.com/props
 */
const breakpoints = [576, 768, 992, 1200];
const mq = breakpoints.map((bp) => `@media (min-width: ${bp}px)`);

function DropdownIndicator<T>(props: DropdownIndicatorProps<T, false>) {
  const { selectProps } = props;

  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.DropdownIndicator {...props}>
      {selectProps.menuIsOpen ? ChevronUp : ChevronDown}
    </components.DropdownIndicator>
  );
}

function Option<T>(props: OptionProps<T, false>) {
  const { isSelected, isFocused, children, isDisabled, ...otherProps } = props;

  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.Option
      {...otherProps}
      isSelected={false}
      isFocused={false}
      isDisabled={isDisabled}
      className={cx('option', {
        'option--selected': isSelected,
        'option--focused': isFocused,
        'option--disabled': isDisabled,
      })}>
      {children}
    </components.Option>
  );
}

export interface IOption {
  value: string | number | null;
  label: string;
}

interface Props<T extends IOption> {
  options: Array<T>;
  onChange?: (value: T | null) => void;
  onInputChange?: (inputValue: string) => void;
  onRemove?: () => void;
  showLabel?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  defaultOption?: T | string | number;
  value?: T | string | number;
  fullWidth?: boolean;
  isCreateable?: boolean;
  isClearable?: boolean;
  validateNewItem?: (inputValue: string, selectValue: unknown) => boolean;
  newOption?: (inputValue: string) => void;
}

export { type Props as DropdownProps };

function DropdownTT<T extends IOption>(props: Props<T>) {
  const {
    options,
    placeholder,
    disabled,
    id,
    onChange,
    onInputChange,
    onRemove,
    defaultOption,
    fullWidth,
    value,
    isCreateable,
    validateNewItem,
    newOption,
    ...otherProps
  } = props;

  const DropdownStyles = {
    input: (styles) => ({
      ...styles,
      margin: '0',
      paddingTop: '12.5px',
      paddingBottom: '12.5px',
      color: COLORS.black,
      paddingLeft: '1rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    placeholder: (styles) => ({
      ...styles,
      color: 'inherit',
      fontSize: '0.75rem',
      marginLeft: '0',
      paddingLeft: '0.875rem',
      lineHeight: '1.5rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [mq[2]]: {
        ...styles[mq[2]],
        fontSize: '0.875rem',
        lineHeight: '1.375rem',
      },
    }),
    singleValue: (styles, { isDisabled }) => ({
      ...styles,
      color: isDisabled ? COLORS.disabledText : COLORS.black,
      paddingLeft: '1rem',
      marginLeft: '0',
    }),
    menu: (styles) => ({
      ...styles,
      boxShadow: 'none',
      outline: `1px solid ${COLORS.borderGrey}`,
      zIndex: '2',
    }),
    menuList: (styles) => ({
      ...styles,
      padding: '0.5rem 0',
    }),
    valueContainer: (styles) => ({
      ...styles,
      paddingTop: '0',
      paddingBottom: '0',
    }),
    container: (styles) => ({
      ...styles,
      display: 'inline-block',
      width: 'calc(100% - 3.5em)', // allow for X
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      minHeight: cx('formElementHeight'),
      [mq[2]]: {
        ...styles[mq[2]],
        fontSize: '0.875rem',
        lineHeight: '1.375rem',
      },
    }),
    dropdownIndicator: (_, { isDisabled }) => ({
      paddingRight: '1.25rem',
      color: isDisabled ? COLORS.disabledGrey : COLORS.black,
    }),
    option: () => ({
      //Option styled with scss.
    }),
    control: (styles, { isFocused, isDisabled }) => ({
      ...styles,
      color: isDisabled ? COLORS.disabledText : COLORS.mediumGrey,
      fontSize: '0.875rem',
      transitionDuration: '0s',
      margin: '0',
      outline: isDisabled ? 'none' : isFocused ? `5px solid ${COLORS.lightGrey}` : '5px solid transparent',
      border: isDisabled
        ? `1px solid ${COLORS.disabledGrey}`
        : isFocused
          ? `1px solid ${COLORS.borderGrey}`
          : `1px solid ${COLORS.borderGrey}`,
      background: isDisabled ? COLORS.disabledGrey : COLORS.white,
      borderRadius: '2px',
      height: cx('formElementHeight'),
      '&:hover': {
        outline: isDisabled ? 'none' : isFocused ? `5px solid ${COLORS.lightGrey}` : `2px solid ${COLORS.black}`,
        border: isDisabled
          ? `1px solid ${COLORS.disabledGrey}`
          : isFocused
            ? `1px solid ${COLORS.borderGrey}`
            : '1px solid transparent',
        cursor: 'pointer',
        color: 'black',
      },
    }),
    indicatorSeparator: (styles) => ({ ...styles, display: 'none' }),
  };

  let selectValue: T | undefined;
  if (typeof value === 'string' || typeof value === 'number') {
    selectValue = options.find((option) => option.value === value);
  } else {
    selectValue = value;
  }

  let selectDefault: T | undefined;
  if (typeof defaultOption === 'string' || typeof defaultOption === 'number') {
    selectDefault = (options || []).find((option) => option.value === defaultOption);
  } else if (!defaultOption && options) {
    selectDefault = (options || []).find((option) => option.value === '');
  } else {
    selectDefault = defaultOption;
  }

  const componentProps = {
    styles: DropdownStyles,
    onChange,
    onInputChange,
    options,
    'aria-labelledby': `${id}-label`,
    isDisabled: disabled,
    placeholder,
    inputId: id,
    components: {
      DropdownIndicator,
      Option,
    },
    searchable: true,
    ...otherProps,
    defaultValue: selectDefault,
    value: selectValue,
  };

  return (
    <div className={cx('form-element', 'dropdown-wrapper', 'dropdown-wrapper-fullwidth')}>
      <button onClick={onRemove} className={cx('close-button')}>
        <i className='fa-solid fa-xmark'></i>
      </button>
      {isCreateable ? (
        <Createable
          createOptionPosition='last'
          isValidNewOption={validateNewItem}
          onCreateOption={newOption}
          {...componentProps}
        />
      ) : (
        <Select {...componentProps} />
      )}
    </div>
  );
}

export default DropdownTT;
